import React, { useState } from 'react';
import { Text, Group, Button, Menu, Modal, Select, Stack, Badge } from '@mantine/core';
import { IconExternalLink } from '@tabler/icons-react';
import { EditableText } from '@ayocore/platform/client/components/EditableText';
import { ViewPanel, InfoRow } from '@ayocore/platform/client/components/EntityViewLayout';
import logger from '@ayocore/platform/common/helpers/logger';
import { AddIconButton, DeleteIconButton } from '@ayocore/platform/client/components/elements';
import { useSurfaceZIndex } from '@ayocore/platform/client/components/surfaceZIndex';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import { contactChannelRepository } from '@ayocore/erp/crm/client/rpc';
import { CHANNEL_TYPES } from '@ayocore/erp/crm/common/channel-types';
import contactChannelSchemaDefinition from '@ayocore/erp/crm/schemas/contact-channel.schema';
import type {
  ContactChannel,
  ContactChannelCreate,
} from '@ayocore/erp/crm/schemas/contact-channel.schema';
import { formatPhone } from '@ayocore/platform/client/components/EditableText/formatters/formatPhone';

interface ChannelManagerProps {
  entityUuid: string;
  contactChannels: ContactChannel[];
  onChannelsChanged: () => void;
  /**
   * Можно ли заводить новые каналы. false — кнопки и плейсхолдеры добавления
   * скрыты. Дефолт true — экраны без своей политики работают как раньше.
   */
  canAdd?: boolean;
  /**
   * Можно ли править конкретный канал (правка значения, описания, удаление).
   * Право считает сервер по каждой записи: политика может разрешать правку
   * только что добавленного канала на «закрытой» карточке. Дефолт — всё можно.
   */
  canEditChannel?: (channel: ContactChannel) => boolean;
}

const VALUE_MIN_WIDTH = 180; // Минимальная ширина колонки значения — выравнивает начало описаний

/** Типы каналов, которые всегда отображаются, даже если канал ещё не создан */
const DEFAULT_CHANNEL_TYPES = ['phone', 'email'] as const;

/** Типы каналов, доступные для быстрого добавления через inline-строку */
const QUICK_ADD_CHANNEL_TYPES = [
  { value: 'phone', label: 'Телефон' },
  { value: 'email', label: 'Почта' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'website', label: 'Сайт' },
  { value: 'instagram', label: 'Instagram' },
];

const placeholderClass = 'entity-view-placeholder-dimmed';

/** Тип канала «Сайт» — источник истины: CHANNEL_TYPES (common/channel-types.ts). */
const WEBSITE_CHANNEL_TYPE = CHANNEL_TYPES.find(t => t.value === 'website')?.value;

/** Добавляет протокол, если его нет — иначе браузер трактует ссылку как относительную. */
const normalizeWebsiteUrl = (value: string): string => {
  const trimmed = value.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const getContactTypeLabel = (type: string): string => {
  const labels: Record<string, string> = {
    phone: 'Телефон',
    email: 'Почта',
    whatsapp: 'WhatsApp',
    telegram: 'Telegram',
    skype: 'Skype',
    viber: 'Viber',
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    website: 'Сайт',
    instagram: 'Instagram',
    twitter: 'Twitter',
    youtube: 'YouTube',
  };
  return labels[type] ?? type;
};

export function ChannelManager({
  entityUuid,
  contactChannels,
  onChannelsChanged,
  canAdd = true,
  canEditChannel = () => true,
}: ChannelManagerProps) {
  const [showAddModal, setShowAddModal] = useState(false);
  // Инлайн-модалка (не через useModal): z-index из общего аллокатора, иначе при
  // открытии менеджера внутри дравера (Lead/Partner view) модалка окажется под ним.
  const addModalZIndex = useSurfaceZIndex(showAddModal);
  const [addingDescriptionForId, setAddingDescriptionForId] = useState<number | null>(null);
  const [addingChannelType, setAddingChannelType] = useState<string | null>(null);
  const [addPopoverOpened, setAddPopoverOpened] = useState(false);

  const handleCreateChannel = async (contactType: string, value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    try {
      await contactChannelRepository.addChannelToEntity(entityUuid, {
        contact_type: contactType,
        contact_value: trimmed,
        is_verified: false,
        is_active: true,
      } as ContactChannelCreate);
      onChannelsChanged();
    } catch (error) {
      logger.error('Error creating channel:', error);
    }
  };

  const handleAddChannel = async (values: ContactChannelCreate) => {
    logger.info('🔍 Adding channel with values:', values);
    logger.info('🔍 Entity UUID:', entityUuid);
    try {
      const result = await contactChannelRepository.addChannelToEntity(entityUuid, values);
      logger.info('✅ Channel added successfully:', result);
      logger.info('🔄 Calling onChannelsChanged...');
      onChannelsChanged();
      logger.info('✅ Modal closing...');
      setShowAddModal(false);
      logger.info('✅ All done!');
    } catch (error) {
      logger.error('❌ Error adding channel:', error);
      throw error; // Re-throw to let SchemaForm handle it
    }
  };

  const handleInlineSaveDescription = async (channelId: number, newValue: string) => {
    try {
      await contactChannelRepository.update(channelId, { description: newValue.trim() || null });
      onChannelsChanged();
    } catch (error) {
      logger.error('Error updating channel description:', error);
    }
  };

  const handleInlineSave = async (channelId: number, newValue: string) => {
    try {
      await contactChannelRepository.update(channelId, { contact_value: newValue.trim() });
      onChannelsChanged();
    } catch (error) {
      logger.error('Error updating channel:', error);
    }
  };

  const hoverStyles = `
    .contact-item:hover .delete-icon { opacity: 1 !important; }
    .contact-item:hover .add-description-hint { opacity: 1 !important; }
  `;

  const handleDeleteChannel = async (channelId: number) => {
    if (!confirm('Удалить этот контакт?')) return;

    try {
      await contactChannelRepository.removeChannelFromEntity(entityUuid, channelId);
      onChannelsChanged();
    } catch (error) {
      logger.error('Error deleting channel:', error);
    }
  };

  // Каналы отсортированы: DEFAULT_CHANNEL_TYPES идут в заданном порядке, остальные — после
  const sortedChannels = [...contactChannels].sort((a, b) => {
    const aIdx = DEFAULT_CHANNEL_TYPES.indexOf(
      a.contact_type as (typeof DEFAULT_CHANNEL_TYPES)[number]
    );
    const bIdx = DEFAULT_CHANNEL_TYPES.indexOf(
      b.contact_type as (typeof DEFAULT_CHANNEL_TYPES)[number]
    );
    if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
    if (aIdx !== -1) return -1;
    if (bIdx !== -1) return 1;
    return 0;
  });

  // Типы каналов, для которых уже есть хотя бы одна запись
  const existingTypes = new Set(contactChannels.map(ch => ch.contact_type));

  return (
    <>
      <style>{hoverStyles}</style>
      <ViewPanel>
        <Group justify='space-between' align='center' mb='xs'>
          <Text fw={400} style={{ fontSize: 14 }}>
            Контакты организации
          </Text>
          {canAdd && <AddIconButton onClick={() => setShowAddModal(true)} />}
        </Group>

        {/* Существующие каналы */}
        {sortedChannels.map(channel => (
          <InfoRow key={channel.id} className='contact-item'>
            <InfoRow.TitleCol>{getContactTypeLabel(channel.contact_type)}</InfoRow.TitleCol>
            <InfoRow.ValueCol>
              <Group
                gap='xs'
                align='flex-start'
                wrap='nowrap'
                style={{
                  minWidth: 0,
                  flex: 1,
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                }}
              >
                <Group gap='xs' align='flex-start' wrap='nowrap' style={{ minWidth: 0, flex: 1 }}>
                  <EditableText
                    value={channel.contact_value}
                    size='sm'
                    canEdit={canEditChannel(channel)}
                    allowEmptySave
                    formatValue={channel.contact_type === 'phone' ? formatPhone : undefined}
                    onSave={v => handleInlineSave(channel.id, v)}
                    style={{
                      minWidth: VALUE_MIN_WIDTH,
                      flexShrink: 0,
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    }}
                  />
                  {channel.contact_type === WEBSITE_CHANNEL_TYPE && channel.contact_value && (
                    <a
                      href={normalizeWebsiteUrl(channel.contact_value)}
                      target='_blank'
                      rel='noopener noreferrer'
                      onClick={e => e.stopPropagation()}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        color: 'var(--mantine-color-blue-6)',
                        flexShrink: 0,
                      }}
                    >
                      <IconExternalLink size={14} />
                    </a>
                  )}
                  {(() => {
                    const desc = channel.description ?? '';
                    const isAdding = addingDescriptionForId === channel.id;
                    if (desc) {
                      return (
                        <EditableText
                          value={desc}
                          size='sm'
                          canEdit={canEditChannel(channel)}
                          allowEmptySave
                          onSave={v => handleInlineSaveDescription(channel.id, v)}
                          style={{ flex: 1, minWidth: 0, color: 'var(--mantine-color-dimmed)' }}
                        />
                      );
                    }
                    if (isAdding) {
                      return (
                        <EditableText
                          value=''
                          size='sm'
                          canEdit
                          placeholder='Описание...'
                          defaultEditing
                          onSave={v => {
                            setAddingDescriptionForId(null);
                            void handleInlineSaveDescription(channel.id, v);
                          }}
                          onCancel={() => setAddingDescriptionForId(null)}
                          style={{ flex: 1, minWidth: 0, color: 'var(--mantine-color-dimmed)' }}
                        />
                      );
                    }
                    // Подсказка «добавить описание» — affordance правки, без прав не нужна
                    if (!canEditChannel(channel)) return null;
                    return (
                      <Text
                        component='span'
                        size='sm'
                        className='add-description-hint'
                        style={{
                          flex: 1,
                          minWidth: 0,
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                          color: 'var(--mantine-color-gray-5)',
                          fontStyle: 'italic',
                          cursor: 'pointer',
                        }}
                        onClick={() => setAddingDescriptionForId(channel.id)}
                      >
                        Добавить описание
                      </Text>
                    );
                  })()}
                  {channel.is_verified && (
                    <Badge size='xs' color='green' variant='dot'>
                      ✓
                    </Badge>
                  )}
                </Group>
                {canEditChannel(channel) && (
                  <DeleteIconButton
                    onClick={e => {
                      e.stopPropagation();
                      handleDeleteChannel(channel.id);
                    }}
                    style={{ opacity: 0, transition: 'opacity 0.2s ease' }}
                    className='delete-icon'
                  />
                )}
              </Group>
            </InfoRow.ValueCol>
          </InfoRow>
        ))}

        {/* Placeholder-строки для телефона и почты, если каналов этого типа ещё нет.
            Без прав на правку это пустая строка «Добавить...» — не показываем. */}
        {canAdd &&
          DEFAULT_CHANNEL_TYPES.filter(type => !existingTypes.has(type)).map(type => (
            <InfoRow key={`placeholder-${type}`}>
              <InfoRow.TitleCol>{getContactTypeLabel(type)}</InfoRow.TitleCol>
              <InfoRow.ValueCol>
                <EditableText
                  value=''
                  size='sm'
                  canEdit
                  placeholder='Добавить...'
                  className={placeholderClass}
                  onSave={v => {
                    if (v.trim()) void handleCreateChannel(type, v);
                  }}
                  style={{ minWidth: VALUE_MIN_WIDTH, flexShrink: 0 }}
                />
              </InfoRow.ValueCol>
            </InfoRow>
          ))}

        {/* Быстрое добавление нового канала: hover → выбор типа → inline-ввод */}
        {canAdd &&
          (addingChannelType ? (
            <InfoRow>
              <InfoRow.TitleCol>{getContactTypeLabel(addingChannelType)}</InfoRow.TitleCol>
              <InfoRow.ValueCol>
                <EditableText
                  value=''
                  size='sm'
                  canEdit
                  placeholder='Добавить...'
                  defaultEditing
                  onSave={v => {
                    setAddingChannelType(null);
                    if (v.trim()) void handleCreateChannel(addingChannelType, v);
                  }}
                  onCancel={() => setAddingChannelType(null)}
                  style={{ minWidth: VALUE_MIN_WIDTH, flexShrink: 0 }}
                />
              </InfoRow.ValueCol>
            </InfoRow>
          ) : (
            <Menu
              opened={addPopoverOpened}
              onClose={() => setAddPopoverOpened(false)}
              position='bottom-start'
              offset={4}
              withinPortal
            >
              <Menu.Target>
                <div onClick={() => setAddPopoverOpened(true)} style={{ cursor: 'pointer' }}>
                  <InfoRow>
                    <InfoRow.TitleCol>
                      <Text size='sm' c='dimmed' fs='italic'>
                        + Добавить
                      </Text>
                    </InfoRow.TitleCol>
                  </InfoRow>
                </div>
              </Menu.Target>
              <Menu.Dropdown>
                {QUICK_ADD_CHANNEL_TYPES.map(({ value, label }) => (
                  <Menu.Item key={value} onClick={() => setAddingChannelType(value)}>
                    {label}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          ))}
      </ViewPanel>

      {/* Add Channel Modal */}
      <Modal
        opened={showAddModal}
        onClose={() => setShowAddModal(false)}
        title='Добавить контакт'
        size='md'
        zIndex={addModalZIndex}
      >
        <AddChannelForm onSubmit={handleAddChannel} onCancel={() => setShowAddModal(false)} />
      </Modal>
    </>
  );
}

// Add Channel Form Component
function AddChannelForm({
  onSubmit,
  onCancel,
}: {
  onSubmit: (values: ContactChannelCreate) => void;
  onCancel: () => void;
}) {
  const SchemaForm = useSchemaForm(contactChannelSchemaDefinition.createSchema, {
    onSubmitSuccess: () => {
      SchemaForm.setSuccess('Контакт успешно добавлен!');
    },
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        return SchemaForm.handleSubmit(values => onSubmit(values as ContactChannelCreate))(e);
      }}
    >
      <Stack gap='xs'>
        <SchemaForm.Messages />

        {/* Simple select for contact type */}
        <Select
          label='Тип контакта'
          placeholder='Выберите тип контакта...'
          size='xs'
          data={[
            { value: 'phone', label: 'Телефон' },
            { value: 'email', label: 'Email' },
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'telegram', label: 'Telegram' },
            { value: 'website', label: 'Сайт' },
          ]}
          {...SchemaForm.Field.contact_type.props}
        />

        <SchemaForm.Field.contact_value size='xs' />
        <SchemaForm.Field.description size='xs' />

        <Group justify='flex-end' gap='xs'>
          <Button variant='light' onClick={onCancel} size='xs'>
            Отмена
          </Button>
          <SchemaForm.buttons.submit label='Добавить' size='xs' />
        </Group>
      </Stack>
    </form>
  );
}

// Edit Channel Form Component
export function EditChannelForm({
  channel,
  onSubmit,
  onCancel,
}: {
  channel: ContactChannel;
  onSubmit: (values: Parameters<typeof contactChannelRepository.update>[1]) => void;
  onCancel: () => void;
}) {
  // Guard against undefined channel
  if (!channel) {
    return <Text c='red'>Ошибка: контакт не найден</Text>;
  }

  const SchemaForm = useSchemaForm(contactChannelSchemaDefinition.updateSchema, {
    // onSubmit,
    onSubmitSuccess: () => {
      SchemaForm.setSuccess('Контакт успешно обновлен!');
    },
  });

  // Set initial data when channel is available
  React.useEffect(() => {
    if (channel) {
      SchemaForm.setInitialData({
        contact_type: channel.contact_type,
        contact_value: channel.contact_value,
        description: channel.description || '',
      });
    }
  }, [channel]);

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        return SchemaForm.handleSubmit(values =>
          onSubmit(values as Parameters<typeof contactChannelRepository.update>[1])
        )(e);
      }}
    >
      <Stack gap='md'>
        <SchemaForm.Messages />

        {/* Simple select for contact type */}
        <Select
          label='Тип контакта'
          placeholder='Выберите тип контакта...'
          data={[
            { value: 'phone', label: 'Телефон' },
            { value: 'email', label: 'Email' },
            { value: 'whatsapp', label: 'WhatsApp' },
            { value: 'telegram', label: 'Telegram' },
            { value: 'website', label: 'Сайт' },
          ]}
          {...SchemaForm.Field.contact_type.props}
        />

        <SchemaForm.Field.contact_value />
        <SchemaForm.Field.description />

        <Group justify='flex-end' gap='sm'>
          <Button variant='light' onClick={onCancel}>
            Отмена
          </Button>
          <SchemaForm.buttons.submit label='Сохранить' />
        </Group>
      </Stack>
    </form>
  );
}
